import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Egg, 
  Calendar, 
  Plus, 
  Save,
  Trash2,
  Package,
  ArrowRight,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  History,
  User,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DailyEggProductionByPen = () => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isManager = user.role === 'manager';
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPen, setSelectedPen] = useState('1');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showAllPens, setShowAllPens] = useState(false);
  const [showCrackAudit, setShowCrackAudit] = useState(false);
  const [crackAuditData, setCrackAuditData] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);
  
  // Form data for selected pen
  const [formData, setFormData] = useState({
    extra_large_crates: '',
    extra_large_pieces: '',
    extra_large_crack_crates: '',
    extra_large_crack_pieces: '',
    large_crates: '',
    large_pieces: '',
    large_crack_crates: '',
    large_crack_pieces: '',
    medium_crates: '',
    medium_pieces: '',
    medium_crack_crates: '',
    medium_crack_pieces: '',
    small_crates: '',
    small_pieces: '',
    small_crack_crates: '',
    small_crack_pieces: '',
    damaged_crates: '',
    damaged_pieces: '',
    notes: ''
  });

  // Pens array: 1-6 regular pens, 7 is Brooding Pen, 8 is Grower Pen
  const pens = [1, 2, 3, 4, 5, 6, 7, 8];
  const getPenLabel = (pen) => {
    if (pen === 7) return 'Brooding Pen';
    if (pen === 8) return 'Grower Pen';
    return `Pen ${pen}`;
  };
  
  const eggSizes = [
    { key: 'extra_large', label: 'Extra Large', color: 'bg-purple-100 border-purple-300 text-purple-800', hasCrack: true },
    { key: 'large', label: 'Large', color: 'bg-blue-100 border-blue-300 text-blue-800', hasCrack: true },
    { key: 'medium', label: 'Medium', color: 'bg-green-100 border-green-300 text-green-800', hasCrack: true },
    { key: 'small', label: 'Small', color: 'bg-yellow-100 border-yellow-300 text-yellow-800', hasCrack: true },
    { key: 'damaged', label: 'Damaged', color: 'bg-red-100 border-red-300 text-red-800', hasCrack: false }
  ];

  useEffect(() => {
    fetchDailySummary();
  }, [selectedDate]);

  useEffect(() => {
    // Load pen data when pen selection changes
    if (summary?.pen_productions?.[parseInt(selectedPen)]) {
      const penData = summary.pen_productions[parseInt(selectedPen)];
      setFormData({
        extra_large_crates: penData.extra_large_crates?.toString() || '',
        extra_large_pieces: penData.extra_large_pieces?.toString() || '',
        extra_large_crack_crates: penData.extra_large_crack_crates?.toString() || '',
        extra_large_crack_pieces: penData.extra_large_crack_pieces?.toString() || '',
        large_crates: penData.large_crates?.toString() || '',
        large_pieces: penData.large_pieces?.toString() || '',
        large_crack_crates: penData.large_crack_crates?.toString() || '',
        large_crack_pieces: penData.large_crack_pieces?.toString() || '',
        medium_crates: penData.medium_crates?.toString() || '',
        medium_pieces: penData.medium_pieces?.toString() || '',
        medium_crack_crates: penData.medium_crack_crates?.toString() || '',
        medium_crack_pieces: penData.medium_crack_pieces?.toString() || '',
        small_crates: penData.small_crates?.toString() || '',
        small_pieces: penData.small_pieces?.toString() || '',
        small_crack_crates: penData.small_crack_crates?.toString() || '',
        small_crack_pieces: penData.small_crack_pieces?.toString() || '',
        damaged_crates: penData.damaged_crates?.toString() || '',
        damaged_pieces: penData.damaged_pieces?.toString() || '',
        notes: penData.notes || ''
      });
    } else {
      // Reset form for empty pen
      setFormData({
        extra_large_crates: '',
        extra_large_pieces: '',
        extra_large_crack_crates: '',
        extra_large_crack_pieces: '',
        large_crates: '',
        large_pieces: '',
        large_crack_crates: '',
        large_crack_pieces: '',
        medium_crates: '',
        medium_pieces: '',
        medium_crack_crates: '',
        medium_crack_pieces: '',
        small_crates: '',
        small_pieces: '',
        small_crack_crates: '',
        small_crack_pieces: '',
        damaged_crates: '',
        damaged_pieces: '',
        notes: ''
      });
    }
  }, [selectedPen, summary]);

  const fetchDailySummary = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/daily-egg-summary/${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Failed to load daily summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchCrackAudit = async () => {
    if (!isAdmin && !isManager) return;
    setLoadingAudit(true);
    try {
      const response = await fetch(`${API_URL}/api/crack-audit-trail/summary/${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCrackAuditData(data);
      }
    } catch (error) {
      console.error('Error fetching crack audit:', error);
    } finally {
      setLoadingAudit(false);
    }
  };

  // Fetch crack audit when showing audit section
  useEffect(() => {
    if (showCrackAudit && (isAdmin || isManager)) {
      fetchCrackAudit();
    }
  }, [showCrackAudit, selectedDate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        date: selectedDate,
        pen_number: parseInt(selectedPen),
        extra_large_crates: parseInt(formData.extra_large_crates) || 0,
        extra_large_pieces: parseInt(formData.extra_large_pieces) || 0,
        extra_large_crack_crates: parseInt(formData.extra_large_crack_crates) || 0,
        extra_large_crack_pieces: parseInt(formData.extra_large_crack_pieces) || 0,
        large_crates: parseInt(formData.large_crates) || 0,
        large_pieces: parseInt(formData.large_pieces) || 0,
        large_crack_crates: parseInt(formData.large_crack_crates) || 0,
        large_crack_pieces: parseInt(formData.large_crack_pieces) || 0,
        medium_crates: parseInt(formData.medium_crates) || 0,
        medium_pieces: parseInt(formData.medium_pieces) || 0,
        medium_crack_crates: parseInt(formData.medium_crack_crates) || 0,
        medium_crack_pieces: parseInt(formData.medium_crack_pieces) || 0,
        small_crates: parseInt(formData.small_crates) || 0,
        small_pieces: parseInt(formData.small_pieces) || 0,
        small_crack_crates: parseInt(formData.small_crack_crates) || 0,
        small_crack_pieces: parseInt(formData.small_crack_pieces) || 0,
        damaged_crates: parseInt(formData.damaged_crates) || 0,
        damaged_pieces: parseInt(formData.damaged_pieces) || 0,
        notes: formData.notes
      };

      const response = await fetch(`${API_URL}/api/daily-pen-production`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(`${getPenLabel(parseInt(selectedPen))} production saved successfully`);
        fetchDailySummary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save production');
      }
    } catch (error) {
      console.error('Error saving production:', error);
      toast.error('Failed to save production');
    } finally {
      setSaving(false);
    }
  };

  const formatNumber = (num) => {
    return (num || 0).toLocaleString();
  };

  const SummaryCard = ({ title, data, color, icon: Icon }) => (
    <div className={`rounded-xl border-2 ${color} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-5 h-5" />}
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="grid grid-cols-5 gap-2 text-xs">
        {eggSizes.map(size => {
          const crates = data?.[`${size.key}_crates`] || 0;
          const pieces = data?.[`${size.key}_pieces`] || 0;
          const crackCrates = size.hasCrack ? (data?.[`${size.key}_crack_crates`] || 0) : 0;
          const crackPieces = size.hasCrack ? (data?.[`${size.key}_crack_pieces`] || 0) : 0;
          
          return (
            <div key={size.key} className="text-center">
              <div className="font-medium text-[10px] uppercase tracking-wider mb-1 opacity-70 truncate" title={size.label}>
                {size.key === 'extra_large' ? 'X-LRG' : size.label.substring(0, 6)}
              </div>
              <div className="space-y-0.5">
                <div className="font-bold">{formatNumber(crates)}c</div>
                <div className="text-[10px] opacity-80">{formatNumber(pieces)}p</div>
                {size.hasCrack && (
                  <>
                    <div className="text-orange-600 font-medium text-[10px]">{formatNumber(crackCrates)}cc</div>
                    <div className="text-orange-500 text-[10px]">{formatNumber(crackPieces)}cp</div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const EggInputCard = ({ size }) => (
    <div className={`rounded-xl border-2 ${size.color} p-4`}>
      <h4 className="font-semibold mb-3 flex items-center gap-2">
        <Egg className="w-4 h-4" />
        {size.label}
      </h4>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs opacity-70">Crates</Label>
          <Input
            type="number"
            min="0"
            value={formData[`${size.key}_crates`]}
            onChange={(e) => handleInputChange(`${size.key}_crates`, e.target.value)}
            className="mt-1"
            data-testid={`input-${size.key}-crates`}
          />
        </div>
        <div>
          <Label className="text-xs opacity-70">Pieces</Label>
          <Input
            type="number"
            min="0"
            value={formData[`${size.key}_pieces`]}
            onChange={(e) => handleInputChange(`${size.key}_pieces`, e.target.value)}
            className="mt-1"
            data-testid={`input-${size.key}-pieces`}
          />
        </div>
        {size.hasCrack && (
          <>
            <div>
              <Label className="text-xs text-orange-600">Crack Crates</Label>
              <Input
                type="number"
                min="0"
                value={formData[`${size.key}_crack_crates`]}
                onChange={(e) => handleInputChange(`${size.key}_crack_crates`, e.target.value)}
                className="mt-1 border-orange-200"
                data-testid={`input-${size.key}-crack-crates`}
              />
            </div>
            <div>
              <Label className="text-xs text-orange-600">Crack Pieces</Label>
              <Input
                type="number"
                min="0"
                value={formData[`${size.key}_crack_pieces`]}
                onChange={(e) => handleInputChange(`${size.key}_crack_pieces`, e.target.value)}
                className="mt-1 border-orange-200"
                data-testid={`input-${size.key}-crack-pieces`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="daily-egg-production-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Daily Egg Production by Pen
        </h1>
        <p className="text-lg text-slate-600 mt-2">Record and track egg production across all pens</p>
      </div>

      {/* Date & Pen Selection */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-500" />
            <Label className="text-sm font-medium">Date:</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-44"
              data-testid="date-picker"
            />
          </div>
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-slate-500" />
            <Label className="text-sm font-medium">Select Pen:</Label>
            <Select value={selectedPen} onValueChange={setSelectedPen}>
              <SelectTrigger className="w-32" data-testid="pen-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pens.map(pen => (
                  <SelectItem key={pen} value={pen.toString()}>
                    {getPenLabel(pen)}
                    {summary?.pen_productions?.[pen] && (
                      <span className="ml-2 text-green-500">✓</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={fetchDailySummary}
            disabled={loading}
            className="ml-auto"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Production Entry Form */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Egg className="w-5 h-5 text-emerald-500" />
            Pen {selectedPen} Production Entry
          </h2>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-production-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Production'}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {eggSizes.map(size => (
            <EggInputCard key={size.key} size={size} />
          ))}
        </div>

        <div className="mt-4">
          <Label className="text-sm font-medium">Notes (Optional)</Label>
          <Input
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Any additional notes for this pen's production..."
            className="mt-1"
            data-testid="notes-input"
          />
        </div>
      </div>

      {/* Daily Summary Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-500" />
            Daily Summary - {selectedDate}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllPens(!showAllPens)}
          >
            {showAllPens ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
            {showAllPens ? 'Hide' : 'Show'} All Pens
          </Button>
        </div>

        {/* All Pens Breakdown (Collapsible) */}
        {showAllPens && (
          <div className="mb-6 pb-6 border-b">
            <h3 className="text-sm font-medium text-slate-600 mb-3">Production by Pen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {pens.map(pen => {
                const penData = summary?.pen_productions?.[pen];
                return (
                  <div
                    key={pen}
                    className={`p-3 rounded-lg border ${
                      penData ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="font-semibold text-sm mb-2 flex items-center justify-between">
                      <span>{getPenLabel(pen)}</span>
                      {penData ? (
                        <span className="text-emerald-600 text-xs">Recorded</span>
                      ) : (
                        <span className="text-slate-400 text-xs">No data</span>
                      )}
                    </div>
                    {penData && (
                      <div className="grid grid-cols-5 gap-1 text-[10px]">
                        {eggSizes.map(size => (
                          <div key={size.key} className="text-center">
                            <div className="font-medium uppercase opacity-60">{size.label.substring(0, 2)}</div>
                            <div>{penData[`${size.key}_crates`] || 0}c</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="space-y-4">
          {/* Opening Inventory */}
          <SummaryCard
            title="Opening Inventory (Previous Day Closing)"
            data={summary?.opening_inventory}
            color="bg-slate-50 border-slate-200 text-slate-700"
            icon={Package}
          />

          {/* Total Production */}
          <SummaryCard
            title="Total Daily Production (All Pens)"
            data={summary?.total_production}
            color="bg-emerald-50 border-emerald-200 text-emerald-800"
            icon={Plus}
          />

          {/* Available */}
          <SummaryCard
            title="Available (Opening + Production)"
            data={summary?.available}
            color="bg-blue-50 border-blue-200 text-blue-800"
            icon={Package}
          />

          {/* Total Pickups */}
          <SummaryCard
            title="Total Customer Pickups (Deduction)"
            data={summary?.total_pickups}
            color="bg-orange-50 border-orange-200 text-orange-800"
            icon={ArrowRight}
          />

          {/* Closing Inventory - Highlighted */}
          <div className="rounded-xl border-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-5">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-6 h-6" />
              <h3 className="font-bold text-lg">Daily Closing Egg Inventory</h3>
              <span className="text-xs bg-white/20 px-2 py-1 rounded-full ml-auto">
                Carried to next day
              </span>
            </div>
            <div className="grid grid-cols-5 gap-3 text-sm">
              {eggSizes.map(size => {
                const closing = summary?.closing_inventory || {};
                let rawCrates = closing[`${size.key}_crates`] || 0;
                let rawPieces = closing[`${size.key}_pieces`] || 0;
                let rawCrackCrates = size.hasCrack ? (closing[`${size.key}_crack_crates`] || 0) : 0;
                let rawCrackPieces = size.hasCrack ? (closing[`${size.key}_crack_pieces`] || 0) : 0;
                
                // Convert pieces >= 30 to crates (30 pieces = 1 crate)
                const extraCrates = Math.floor(rawPieces / 30);
                const crates = rawCrates + extraCrates;
                const pieces = rawPieces % 30;
                
                const extraCrackCrates = Math.floor(rawCrackPieces / 30);
                const crackCrates = rawCrackCrates + extraCrackCrates;
                const crackPieces = rawCrackPieces % 30;
                
                const isNegative = rawCrates < 0 || rawPieces < 0 || rawCrackCrates < 0 || rawCrackPieces < 0;
                
                return (
                  <div key={size.key} className={`text-center p-2 rounded-lg ${isNegative ? 'bg-red-500/30' : 'bg-white/10'}`}>
                    <div className="font-medium text-xs uppercase tracking-wider mb-2 opacity-80">
                      {size.label}
                    </div>
                    <div className="space-y-1">
                      <div className="text-xl font-bold">{formatNumber(crates)}</div>
                      <div className="text-xs opacity-80">crates</div>
                      <div className="font-semibold">{formatNumber(pieces)}</div>
                      <div className="text-xs opacity-80">pieces</div>
                      {size.hasCrack && (
                        <>
                          <div className="pt-1 border-t border-white/20">
                            <div className="text-orange-200 font-medium">{formatNumber(crackCrates)}c</div>
                            <div className="text-orange-200 text-xs">{formatNumber(crackPieces)}p</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Warning if any negative values */}
            {summary?.closing_inventory && Object.values(summary.closing_inventory).some(v => typeof v === 'number' && v < 0) && (
              <div className="mt-4 p-2 bg-red-500/30 rounded-lg text-sm flex items-center gap-2">
                <span>⚠️</span>
                <span>Warning: Negative inventory detected. Pickups exceed available stock for some sizes.</span>
              </div>
            )}
          </div>

          {/* All Cracks Aggregate Block */}
          <div data-testid="all-cracks-block" className="rounded-xl border-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-5">
            <div className="flex items-center gap-2 mb-4">
              <Egg className="w-6 h-6" />
              <h3 className="font-bold text-lg">All Cracks (Aggregated)</h3>
              <span className="text-xs bg-white/20 px-2 py-1 rounded-full ml-auto">
                Combined from all sizes
              </span>
            </div>
            
            {/* All Cracks Summary Row */}
            <div className="grid grid-cols-5 gap-2 mb-4 text-xs">
              <div data-testid="all-cracks-opening" className="text-center p-2 bg-white/10 rounded-lg">
                <div className="opacity-70 mb-1">Opening</div>
                <div className="font-bold text-sm">{formatNumber(summary?.all_cracks?.opening || 0)}</div>
                <div className="opacity-70">pieces</div>
              </div>
              <div data-testid="all-cracks-production" className="text-center p-2 bg-white/10 rounded-lg">
                <div className="opacity-70 mb-1">+ Production</div>
                <div className="font-bold text-sm text-emerald-200">{formatNumber(summary?.all_cracks?.production || 0)}</div>
                <div className="opacity-70">pieces</div>
              </div>
              <div data-testid="all-cracks-adjustments" className="text-center p-2 bg-white/10 rounded-lg">
                <div className="opacity-70 mb-1">+/- Adjust</div>
                <div className={`font-bold text-sm ${(summary?.all_cracks?.adjustments || 0) < 0 ? 'text-red-200' : 'text-blue-200'}`}>
                  {formatNumber(summary?.all_cracks?.adjustments || 0)}
                </div>
                <div className="opacity-70">pieces</div>
              </div>
              <div data-testid="all-cracks-pickups" className="text-center p-2 bg-white/10 rounded-lg">
                <div className="opacity-70 mb-1">- Pickups</div>
                <div className="font-bold text-sm text-red-200">{formatNumber(summary?.all_cracks?.pickups || 0)}</div>
                <div className="opacity-70">pieces</div>
              </div>
              <div data-testid="all-cracks-closing" className="text-center p-2 bg-white/20 rounded-lg border border-white/30">
                <div className="opacity-70 mb-1">= Closing</div>
                <div className={`font-bold text-lg ${(summary?.all_cracks?.closing || 0) < 0 ? 'text-red-300' : ''}`}>
                  {formatNumber(summary?.all_cracks?.closing || 0)}
                </div>
                <div className="opacity-70">pieces</div>
              </div>
            </div>

            {/* Crates + Pieces Display */}
            {(() => {
              const totalPieces = summary?.all_cracks?.closing || 0;
              const crates = Math.floor(Math.abs(totalPieces) / 30);
              const pieces = Math.abs(totalPieces) % 30;
              const isNegative = totalPieces < 0;
              
              return (
                <div data-testid="all-cracks-converted" className={`p-4 rounded-lg ${isNegative ? 'bg-red-500/30' : 'bg-white/10'} text-center`}>
                  <div className="text-sm opacity-80 mb-2">Closing Balance (Converted)</div>
                  <div className="flex items-center justify-center gap-4">
                    <div>
                      <span data-testid="all-cracks-converted-crates" className="text-3xl font-bold">{isNegative ? '-' : ''}{formatNumber(crates)}</span>
                      <span className="text-sm ml-1 opacity-80">crates</span>
                    </div>
                    <div className="text-2xl opacity-50">+</div>
                    <div>
                      <span data-testid="all-cracks-converted-pieces" className="text-3xl font-bold">{formatNumber(pieces)}</span>
                      <span className="text-sm ml-1 opacity-80">pieces</span>
                    </div>
                  </div>
                  <div data-testid="all-cracks-total-pieces" className="text-xs opacity-60 mt-2">
                    (Total: {isNegative ? '-' : ''}{formatNumber(Math.abs(totalPieces))} pieces)
                  </div>
                </div>
              );
            })()}

            {/* Warning if negative */}
            {(summary?.all_cracks?.closing || 0) < 0 && (
              <div className="mt-4 p-2 bg-red-500/30 rounded-lg text-sm flex items-center gap-2">
                <span>⚠️</span>
                <span>Warning: Negative crack inventory. More cracks given out than available.</span>
              </div>
            )}
          </div>

          {/* Crack Audit Trail Section - Admin/Manager only */}
          {(isAdmin || isManager) && (
            <div data-testid="crack-audit-section" className="rounded-xl border bg-white shadow-sm">
              {/* Header - Clickable to toggle */}
              <button
                onClick={() => setShowCrackAudit(!showCrackAudit)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-800">Crack Deduction Audit Trail</h3>
                  {crackAuditData && crackAuditData.total_entries > 0 && (
                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                      {crackAuditData.total_entries} entries
                    </span>
                  )}
                </div>
                {showCrackAudit ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>

              {/* Audit Content */}
              {showCrackAudit && (
                <div className="border-t p-4">
                  {loadingAudit ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
                      <span className="ml-2 text-gray-500">Loading audit trail...</span>
                    </div>
                  ) : crackAuditData ? (
                    <div className="space-y-4">
                      {/* Summary Stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-orange-50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-orange-700">
                            {formatNumber(crackAuditData.total_crack_pieces_deducted)}
                          </div>
                          <div className="text-xs text-orange-600">Total Cracks Given</div>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-blue-700">
                            {crackAuditData.unique_customers}
                          </div>
                          <div className="text-xs text-blue-600">Customers</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-gray-700">
                            {crackAuditData.total_entries}
                          </div>
                          <div className="text-xs text-gray-600">Audit Entries</div>
                        </div>
                      </div>

                      {/* Recent Entries */}
                      {crackAuditData.recent_entries && crackAuditData.recent_entries.length > 0 ? (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-gray-700">Recent Activity</h4>
                          <div className="max-h-64 overflow-y-auto space-y-2">
                            {crackAuditData.recent_entries.map((entry, idx) => (
                              <div
                                key={entry.id || idx}
                                data-testid={`audit-entry-${idx}`}
                                className={`p-3 rounded-lg border text-sm ${
                                  entry.action === 'pickup_deleted' 
                                    ? 'bg-red-50 border-red-200' 
                                    : entry.action === 'pickup_updated'
                                    ? 'bg-yellow-50 border-yellow-200'
                                    : 'bg-green-50 border-green-200'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <User className="w-4 h-4 text-gray-500" />
                                      <span className="font-medium">{entry.customer_name}</span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        entry.action === 'pickup_deleted' 
                                          ? 'bg-red-100 text-red-700' 
                                          : entry.action === 'pickup_updated'
                                          ? 'bg-yellow-100 text-yellow-700'
                                          : 'bg-green-100 text-green-700'
                                      }`}>
                                        {entry.action === 'pickup_created' ? 'New Pickup' 
                                          : entry.action === 'pickup_updated' ? 'Updated' 
                                          : 'Deleted'}
                                      </span>
                                    </div>
                                    <div className="mt-1 text-gray-600">
                                      <span className="font-semibold text-orange-700">
                                        {formatNumber(entry.total_crack_pieces)} pieces
                                      </span>
                                      <span className="text-gray-400 mx-1">|</span>
                                      <span className="text-xs">
                                        XL: {entry.extra_large_crack_crates}c/{entry.extra_large_crack_pieces}p,
                                        L: {entry.large_crack_crates}c/{entry.large_crack_pieces}p,
                                        M: {entry.medium_crack_crates}c/{entry.medium_crack_pieces}p,
                                        S: {entry.small_crack_crates}c/{entry.small_crack_pieces}p
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-right text-xs text-gray-500">
                                    <div className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {new Date(entry.created_at).toLocaleTimeString()}
                                    </div>
                                    <div className="mt-0.5">by {entry.performed_by_name}</div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p>No crack deductions recorded for this date</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <p>Failed to load audit data</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyEggProductionByPen;
